import { useEffect } from 'react';
import { Accordion, Box, Heading, Paragraph, Text } from '@zillow/constellation-10';
import { formatDate } from '@zg-rentals/formatters';
import { getCreditAccounts, type PaymentAccount, pickPaymentHistory } from '@zg-rentals/applications-utils';
import { type CreditReportType } from '@zg-rentals/ts-capps-client';
import { countReportData } from '../../utils/report-utils';
import type { EmitMetric } from '../../models/report-models';
import { ReportType } from '../../models/report-models';
import { PaymentHistoryGrid } from './PaymentHistoryGrid';

type PaymentHistoryFlags = {
  showPaymentHistoryJ9: boolean;
  showPaymentHistoryLegacy: boolean;
};

export function CreditReportAccounts({
  creditReport,
  emitMetric,
  showPaymentHistoryJ9,
  showPaymentHistoryLegacy,
}: {
  creditReport: CreditReportType;
  emitMetric: EmitMetric;
} & PaymentHistoryFlags) {
  const { openAccounts, closedAccounts } = getCreditAccounts(creditReport);
  const averageAgeOpenAccounts = creditReport.averageOpenAccountsAge;
  const oldestAccountAge = creditReport.oldestAccountAge;
  const hasAccounts = openAccounts.length > 0 || closedAccounts.length > 0;

  useEffect(() => {
    countReportData(ReportType.CreditReport, 'openAccounts', openAccounts?.length, emitMetric);
    countReportData(ReportType.CreditReport, 'closedAccounts', closedAccounts?.length, emitMetric);
  }, []);

  return (
    <>
      <Box css={{ marginBottom: 'sm' }}>
        <Heading level={3} textStyle="heading-xs" css={{ marginTop: 'md' }}>
          Account overview
        </Heading>
        {hasAccounts && <Paragraph textStyle="body-xs">Data displayed is from the last 25 months</Paragraph>}
      </Box>

      {hasAccounts ? (
        <>
          {openAccounts.length > 0 && (
            <>
              <Heading level={4} textStyle="body-lg-bold" css={{ marginBottom: 'sm' }}>
                Open accounts
              </Heading>
              <AccountAccordion
                accounts={openAccounts}
                showPaymentHistoryJ9={showPaymentHistoryJ9}
                showPaymentHistoryLegacy={showPaymentHistoryLegacy}
              />
              <Box css={{ display: 'flex', justifyContent: 'center', marginTop: 'xs', marginBottom: 'xs' }}>
                <Text textStyle="body-sm">
                  Average age of open accounts: {averageAgeOpenAccounts ? averageAgeOpenAccounts : 'Unavailable'}
                </Text>
              </Box>
            </>
          )}
          {closedAccounts.length > 0 && (
            <>
              <Heading level={4} textStyle="body-lg-bold" css={{ marginBottom: 'sm' }}>
                Closed accounts
              </Heading>
              <AccountAccordion
                accounts={closedAccounts}
                showPaymentHistoryJ9={showPaymentHistoryJ9}
                showPaymentHistoryLegacy={showPaymentHistoryLegacy}
              />
            </>
          )}
          {oldestAccountAge && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: 'xs' }}>
              <Text textStyle="body-sm">Age of oldest account: {oldestAccountAge}</Text>
            </Box>
          )}
        </>
      ) : (
        <Paragraph textStyle="body-sm">None found</Paragraph>
      )}
    </>
  );
}

function AccountAccordion({
  accounts,
  showPaymentHistoryJ9,
  showPaymentHistoryLegacy,
}: {
  accounts: Array<PaymentAccount>;
} & PaymentHistoryFlags) {
  return (
    <Accordion.Root>
      {accounts.map((account, index) => (
        <Accordion.Item key={index} value={`${index}`}>
          <Accordion.Header>
            <Paragraph>{account.name}</Paragraph>
            <Paragraph>
              <strong>{account.amount}</strong>
            </Paragraph>
          </Accordion.Header>
          <Accordion.Panel>
            <PaymentAccountContent
              account={account}
              showPaymentHistoryJ9={showPaymentHistoryJ9}
              showPaymentHistoryLegacy={showPaymentHistoryLegacy}
            />
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
}

const PaymentAccountContent = ({
  account,
  showPaymentHistoryJ9,
  showPaymentHistoryLegacy,
}: {
  account: PaymentAccount;
} & PaymentHistoryFlags) => {
  const paymentHistory = pickPaymentHistory(account.paymentHistory, {
    j9: showPaymentHistoryJ9,
    legacy: showPaymentHistoryLegacy,
  });

  return (
    <>
      <AccountValue label="Balance" value={account.amount} />
      <AccountValue label="Open date" value={formatDate(account.openDate, 'numbers')} />
      <AccountValue label="On-time payments" value={account.onTimePayments} />
      <AccountValue label="Late payments" value={account.latePayments} />
      <AccountValue label="Last late payment" value={formatDate(account.lastLatePayment, 'numbers')} />
      {account.accountComment && <AccountValue label="Comment" value={account.accountComment} />}
      {paymentHistory && <PaymentHistoryGrid paymentHistory={paymentHistory} />}
    </>
  );
};

function AccountValue({ label, value }: { label: string; value: string }) {
  return (
    <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', marginBottom: 'xs', gap: 'xs' }}>
      <Box css={{ display: 'grid', gridTemplateColumns: { base: 'span 1', smDown: 'span 2' } }}>
        <Text textStyle="body-sm">{label}</Text>
      </Box>
      <Box css={{ display: 'grid', gridTemplateColumns: { base: 'span 4', smDown: 'span 3' } }}>
        <Text textStyle="body-sm-bold">{value}</Text>
      </Box>
    </Box>
  );
}
